"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Sparkles, Lock } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function EightOfSwordsPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-gray-500/10 border border-gray-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-gray-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-gray-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-gray-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-gray-500">/</span>
            <span className="text-sm">Kılıçlar</span>
            <span className="text-gray-500">/</span>
            <span className="text-sm">Sekiz Kılıç</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Minör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Sekiz Kılıç</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-gray-900/20 to-slate-900/20 rounded-xl p-8 mb-8 border border-gray-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/eight-of-swords.svg"
                  alt="Eight of Swords Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-500 to-slate-700 rounded-full flex items-center justify-center">
                  <Lock className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Eight of Swords</h1>
                  <p className="text-xl text-gray-400">Sekiz Kılıç / Kısıtlama ve Tuzağa Düşme</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Kısıtlama, tuzağa düşme ve çaresizlik hissinin kartı. Zihinsel sınırlar,
                kendini kurban rolünde hissetme ve çıkış yollarını görememe durumunu temsil eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-gray-500/20 text-gray-300 rounded-full text-sm">Kısıtlama</span>
                <span className="px-3 py-1 bg-gray-500/20 text-gray-300 rounded-full text-sm">Tuzak</span>
                <span className="px-3 py-1 bg-gray-500/20 text-gray-300 rounded-full text-sm">Çaresizlik</span>
                <span className="px-3 py-1 bg-gray-500/20 text-gray-300 rounded-full text-sm">Zihinsel sınırlar</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Zihinsel Kısıtlama</h3>
                  <p className="text-slate-400 text-sm">Sınırlı düşünce kalıpları, olumsuz inanışlar ve fobiler.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Tuzağa Düşme</h3>
                  <p className="text-slate-400 text-sm">Çıkış yolu görememek, hapsolmuş hissetmek.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kurban Psikolojisi</h3>
                  <p className="text-slate-400 text-sm">Kendini mağdur görme, güçsüzlük hissi ve pes etme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kaygı ve Korku</h3>
                  <p className="text-slate-400 text-sm">Aşırı kaygı, korku ve endişe durumları.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Özgürleşme</h3>
                  <p className="text-slate-400 text-sm">Sınırlardan kurtulma, zihinsel açılım ve çözümü görme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Güçlenme</h3>
                  <p className="text-slate-400 text-sm">Kendi gücünü keşfetme, kendine güven ve cesaret.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Perspektif Değişimi</h3>
                  <p className="text-slate-400 text-sm">Yeni bakış açıları, farklı yaklaşımlar ve çözüm odaklılık.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yardım Alma</h3>
                  <p className="text-slate-400 text-sm">Destek arama, dışarıdan bakış ve işbirliği.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👤</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Bağlı Figür</h3>
              <p className="text-slate-400 text-sm">Kısıtlanmış kişi, hareket edememe ve çaresizlik.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👁️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Gözleri Bağlı</h3>
              <p className="text-slate-400 text-sm">Görememe, fark edememe ve zihinsel körlük.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">⚔️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Çevreleyici Kılıçlar</h3>
              <p className="text-slate-400 text-sm">Zihinsel engeller, sınırlar ve korku duvarları.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌊</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Su ve Çamur</h3>
              <p className="text-slate-400 text-sm">Duygusal karmaşa, belirsizlik ve batma hissi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🏰</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Uzak Kale</h3>
              <p className="text-slate-400 text-sm">Erişilmez güvenlik, ulaşılamayan hedefler.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🔗</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Gevşek Bağlar</h3>
              <p className="text-slate-400 text-sm">Gerçekte zayıf olan kısıtlamalar, sahte sınırlar.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Kısıtlayıcı ilişkiler</li>
                <li>• Kendini kurban hissetme</li>
                <li>• Aşırı bağımlılık</li>
                <li>• Çıkış yolu arama</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Sıkışmış kariyer</li>
                <li>• İş yerinde kısıtlamalar</li>
                <li>• Yaratıcılık blokajı</li>
                <li>• Finansal tuzaklar</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Zihinsel sınırlar</li>
                <li>• Olumsuz düşünce kalıpları</li>
                <li>• Özgüven eksikliği</li>
                <li>• Kendi kendine sabotaj</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Lock className="h-5 w-5 text-blue-400" />
                Sağlık ve Yaşam
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Stres ve kaygı bozuklukları</li>
                <li>• Psikolojik kısıtlamalar</li>
                <li>• Fobiler ve korkular</li>
                <li>• Hareket kısıtlılığı</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Advice */}
        <div className="bg-gradient-to-r from-gray-800/50 to-slate-700/50 rounded-xl p-6 border border-gray-500/30 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-4">Tavsiye ve Rehberlik</h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-slate-300 leading-relaxed mb-4">
              Sekiz Kılıç, zihinsel sınırların çoğunlukla kendimiz tarafından yaratıldığını hatırlatır.
              Gerçek kısıtlamaların ve algılanan kısıtlamaların farkına varın.
            </p>
            <p className="text-slate-300 leading-relaxed">
              Durum sandığınızdan daha az kısıtlayıcıdır. Farklı perspektiflerden bakın,
              yardım isteyin ve küçük adımlarla harekete geçin. Çıkış yolu her zaman vardır.
            </p>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/swords/seven-of-swords"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Yedi Kılıç
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/swords/nine-of-swords"
            className="inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Dokuz Kılıç
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}